import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { IAttachment } from "../attachment-list/attachment-list.component";
import { KendoAlertService } from "../../kendo-alert";

/**
 * Interface defining the contract for attachment edit components.
 * Implemented by components that handle attachment editing functionality.
 */
export interface IAttachmentEditComponent {
    /**
     * Determines whether deletion of attachments is allowed.
     */
    canDeleteAttachment: boolean;
    
    /**
     * Gets the current attachment being edited.
     */
    get attachment(): IAttachment;
    
    /**
     * Sets the attachment to be edited.
     */
    set attachment(value: IAttachment);
    
    /**
     * Event emitted when an attachment is saved.
     */
    saveAttachmentEvent: EventEmitter<IAttachment>;
    
    /**
     * Event emitted when editing is canceled.
     */
    cancelEdit: EventEmitter<IAttachment>;
    
    /**
     * Event emitted when an attachment is removed.
     */
    attachmentRemove: EventEmitter<IAttachment>;
}

/**
 * A component for editing attachment metadata.
 * 
 * This component provides a user interface for editing attachment descriptions 
 * and performing actions like saving, canceling, or deleting attachments.
 * Used within a dialog to manage attachment properties.
 * 
 * @example
 * ```html
 * <attachment-edit
 *   [attachment]="selectedAttachment"
 *   [canDeleteAttachment]="true"
 *   (saveAttachmentEvent)="onSave($event)"
 *   (cancelEdit)="onCancel($event)"
 *   (attachmentRemove)="onDelete($event)">
 * </attachment-edit>
 * ```
 */
@Component({
    selector: 'attachment-edit',
    templateUrl: './attachment-edit.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule
    ]
})
export class AttachmentEditComponent implements IAttachmentEditComponent {
    /**
     * @param kendoAlertService Service used for displaying confirmation dialogs.
     */
    constructor(private kendoAlertService: KendoAlertService) { }

    /**
     * Determines whether the delete button should be displayed and enabled.
     * @default false
     */
    @Input() canDeleteAttachment: boolean = false;

    /**
     * @internal 
     * Internal storage for the attachment data.
     */
    data!: IAttachment;
    
    /**
     * @internal
     * Tracks the attachment description for two-way binding in the template.
     */
    description: string | null = null;

    /**
     * Gets the current attachment being edited.
     * @returns The current attachment.
     */
    get attachment(): IAttachment {
        return this.data;
    }

    /**
     * Sets the attachment to be edited and initializes the description field.
     * @param value The attachment to edit.
     */
    @Input()
    set attachment(value: IAttachment) {
        this.data = value;
        this.description = value.Description;
    }

    /**
     * Event emitted when an attachment is saved with updated properties.
     * Emits the updated attachment object.
     */
    @Output() saveAttachmentEvent = new EventEmitter<IAttachment>();
    
    /**
     * Event emitted when editing is canceled.
     * Emits the original attachment object.
     */
    @Output() cancelEdit = new EventEmitter<IAttachment>();
    
    /**
     * Event emitted when an attachment is requested to be removed.
     * Emits the attachment to be removed.
     */
    @Output() attachmentRemove = new EventEmitter<IAttachment>();

    /**
     * Updates the attachment description and emits the save event.
     * This method is called when the user clicks the Save button.
     */
    saveAttachment() {
        this.attachment.Description = this.description;
        this.saveAttachmentEvent.emit(this.attachment);
    }

    /**
     * Cancels the editing process and emits the cancel event.
     * This method is called when the user clicks the Cancel button.
     */
    closeAttachment() {
        this.cancelEdit.emit(this.attachment);
    }

    /**
     * Displays a confirmation dialog and emits the remove event if confirmed.
     * This method is called when the user clicks the Delete button.
     */
    async deleteAttachment() {
        if (await this.kendoAlertService.confirmAsync("Are you sure you want to delete this attachment?")) {
            this.attachmentRemove.emit(this.attachment);
        }
    }
}